import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useState } from 'react';
const METHOD_LABELS = {
    trade_off: 'Best Value Trade-Off',
    lpta: 'Lowest Price Technically Acceptable',
    adjectival: 'Adjectival Ratings',
    point_scoring: 'Point Scoring',
    hybrid: 'Hybrid'
};
const METHOD_COLORS = {
    trade_off: 'bg-blue-100 text-blue-800',
    lpta: 'bg-amber-100 text-amber-800',
    adjectival: 'bg-purple-100 text-purple-800',
    point_scoring: 'bg-green-100 text-green-800',
    hybrid: 'bg-gray-100 text-gray-800'
};
function FactorRow({ factor, depth = 0 }) {
    const [expanded, setExpanded] = useState(depth < 2);
    const hasSubfactors = factor.subfactors && factor.subfactors.length > 0;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start gap-3 py-2 group",
                style: {
                    paddingLeft: `${depth * 24}px`
                },
                children: [
                    hasSubfactors ? /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>setExpanded(!expanded),
                        className: "mt-0.5 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0",
                        children: /*#__PURE__*/ _jsxs("svg", {
                            className: `w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`,
                            fill: "none",
                            viewBox: "0 0 24 24",
                            stroke: "currentColor",
                            strokeWidth: 2,
                            "aria-hidden": "true",
                            role: "img",
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: "Toggle subfactors"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    d: "M9 5l7 7-7 7"
                                })
                            ]
                        })
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: "w-4 flex-shrink-0"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 min-w-0",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 flex-wrap",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium text-gray-900 text-sm",
                                        children: factor.name
                                    }),
                                    factor.importanceRank && factor.importanceRank > 0 && /*#__PURE__*/ _jsxs("span", {
                                        className: "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700",
                                        children: [
                                            "#",
                                            factor.importanceRank
                                        ]
                                    }),
                                    factor.weight && /*#__PURE__*/ _jsx("span", {
                                        className: "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700",
                                        children: factor.weight
                                    })
                                ]
                            }),
                            factor.description && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-gray-500 mt-0.5 line-clamp-2",
                                children: factor.description
                            }),
                            factor.ratings && factor.ratings.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-1 mt-1",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-xs text-gray-400",
                                        children: "Ratings:"
                                    }),
                                    factor.ratings.map((rating)=>/*#__PURE__*/ _jsx("span", {
                                            className: "inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-50 text-gray-600 border border-gray-200",
                                            children: rating
                                        }, rating))
                                ]
                            })
                        ]
                    })
                ]
            }),
            expanded && hasSubfactors && factor.subfactors?.map((sub)=>/*#__PURE__*/ _jsx(FactorRow, {
                    factor: sub,
                    depth: depth + 1
                }, sub.name))
        ]
    });
}
export const EvaluationSchemeCard = /*#__PURE__*/ memo(function EvaluationSchemeCard({ scheme }) {
    const [collapsed, setCollapsed] = useState(false);
    const methodLabel = METHOD_LABELS[scheme.method] || scheme.method || 'Unknown';
    const methodColor = METHOD_COLORS[scheme.method] || METHOD_COLORS.hybrid;
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white rounded-lg border border-gray-200",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                onClick: ()=>setCollapsed(!collapsed),
                className: "w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs("svg", {
                                className: "w-5 h-5 text-blue-600 flex-shrink-0",
                                fill: "none",
                                viewBox: "0 0 24 24",
                                stroke: "currentColor",
                                strokeWidth: 1.5,
                                "aria-hidden": "true",
                                role: "img",
                                children: [
                                    /*#__PURE__*/ _jsx("title", {
                                        children: "Evaluation criteria"
                                    }),
                                    /*#__PURE__*/ _jsx("path", {
                                        strokeLinecap: "round",
                                        strokeLinejoin: "round",
                                        d: "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-sm font-semibold text-gray-900",
                                children: "Evaluation Criteria"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${methodColor}`,
                                children: methodLabel
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-gray-500",
                                children: [
                                    scheme.factors.length,
                                    " factor",
                                    scheme.factors.length !== 1 ? 's' : ''
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("svg", {
                        className: `w-4 h-4 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-180'}`,
                        fill: "none",
                        viewBox: "0 0 24 24",
                        stroke: "currentColor",
                        strokeWidth: 2,
                        "aria-hidden": "true",
                        role: "img",
                        children: [
                            /*#__PURE__*/ _jsx("title", {
                                children: "Toggle collapse"
                            }),
                            /*#__PURE__*/ _jsx("path", {
                                strokeLinecap: "round",
                                strokeLinejoin: "round",
                                d: "M19 9l-7 7-7-7"
                            })
                        ]
                    })
                ]
            }),
            !collapsed && /*#__PURE__*/ _jsxs("div", {
                className: "px-4 pb-4 border-t border-gray-100",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-x-6 gap-y-1 py-3 text-xs text-gray-600",
                        children: [
                            scheme.priceRelation && /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-gray-400",
                                        children: "Price/Technical: "
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-gray-700",
                                        children: scheme.priceRelation
                                    })
                                ]
                            }),
                            scheme.discriminators && scheme.discriminators.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-gray-400",
                                        children: "Discriminators: "
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-gray-700",
                                        children: scheme.discriminators.join(', ')
                                    })
                                ]
                            }),
                            scheme.sourceSections && scheme.sourceSections.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-gray-400",
                                        children: "Source: "
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-gray-700",
                                        children: scheme.sourceSections.join(', ')
                                    })
                                ]
                            })
                        ]
                    }),
                    scheme.methodDetails && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-gray-500 pb-3 border-b border-gray-100",
                        children: scheme.methodDetails
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "divide-y divide-gray-50",
                        children: scheme.factors.map((factor)=>/*#__PURE__*/ _jsx(FactorRow, {
                                factor: factor
                            }, factor.name))
                    })
                ]
            })
        ]
    });
});
